/**
 * Keeps one membership's display and expiry controls independent of modal mutations.
 */
import React from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import { useTranslation } from 'src/arb-wrapper';
import { FormInput, FormSelect } from 'src/components/common/forms';
import { ShrinkableTextValue } from 'src/components/common/ShrinkableTextValue';
import type { BusinessUnitMemberGroupDto } from 'src/store/generated/leela-api-generated';
import { GroupStatusLabelKeys, STATUS_TAG_TYPE } from 'src/utils/groupStatus';
import { getMembershipExpiryPresentation } from 'src/utils/membershipPresentation';

export type ExpiryAction = 'UNCHANGED' | 'CLEAR' | 'SET';

interface EditUserMembershipRowProps {
  control: Control<FieldValues>;
  expiryAction: ExpiryAction;
  expiryActionOptions: Array<{ label: string; value: string }>;
  group: BusinessUnitMemberGroupDto;
  index: number;
  onDelete: (group: BusinessUnitMemberGroupDto) => void;
}

export const EditUserMembershipRow: React.FC<
EditUserMembershipRowProps
> = ({ control, expiryAction, expiryActionOptions, group, index, onDelete }) => {
  const { t } = useTranslation();
  const expiryPresentation = getMembershipExpiryPresentation(
    group.expiresAt,
    String(t('access_management_not_available')),
  );

  return (
    <Box
      data-testid={`edit-user-membership-${group.membershipId}`}
      spacingInset="400"
      type="outline"
    >
      <Column spacing="300">
        <Row
          alignmentHorizontal="justify"
          spacing="200"
          widths={['fill', 'fit']}
        >
          {/* The intermediate flex item needs minWidth: 0 so long group names can wrap. */}
          <div
            data-testid={`edit-user-membership-details-${group.membershipId}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
              gap: '4px',
            }}
          >
            <ShrinkableTextValue
              testId={`edit-user-group-name-${group.membershipId}`}
              type="b300"
            >
              {group.groupName}
            </ShrinkableTextValue>
            {group.status !== 'ACTIVE' && (
              <Tag
                data-testid={`edit-user-group-status-${group.membershipId}`}
                type={STATUS_TAG_TYPE[group.status]}
              >
                {t(GroupStatusLabelKeys[group.status])}
              </Tag>
            )}
            {expiryPresentation.status && (
              <Tag
                data-testid={`edit-user-expiry-status-${group.membershipId}`}
                type={expiryPresentation.status.tagType}
              >
                {t(expiryPresentation.status.labelKey)}
              </Tag>
            )}
            <Text color="secondary" type="b200">
              {t(expiryPresentation.labelKey, expiryPresentation.values)}
            </Text>
          </div>
          <Button
            aria-label={t('edit_user_delete_membership')}
            data-testid={`edit-user-delete-${group.membershipId}`}
            type="icon"
            onClick={() => {
              onDelete(group);
            }}
          >
            <Icon tokens={trashTokens} />
          </Button>
        </Row>

        <FormSelect
          control={control}
          data-testid={`edit-user-expiry-action-${group.membershipId}`}
          disabled={group.status !== 'ACTIVE'}
          label={t('edit_user_expiry_action_label')}
          name={`memberships.${index}.expiryAction`}
          options={expiryActionOptions}
        />
        {expiryAction === 'SET' && (
          <FormInput
            control={control}
            data-testid={`edit-user-expiry-${group.membershipId}`}
            label={t('edit_user_expiry_date_label')}
            name={`memberships.${index}.expiresAt`}
            required
            type="date"
          />
        )}
      </Column>
    </Box>
  );
};
